import { Component } from '@theme/component';
import { fetchConfig, preloadImage, onAnimationEnd, yieldToMainThread } from '@theme/utilities';
import { cartPerformance } from '@theme/performance';
import { morph } from '@theme/morph';
import { CartLinesUpdateEvent, CartErrorEvent, ProductSelectEvent, StandardEvents } from '@shopify/events';

const ERROR_MESSAGE_DISPLAY_DURATION = 10000;
const ERROR_BUTTON_REENABLE_DELAY = 1000;
const SUCCESS_MESSAGE_DISPLAY_DURATION = 5000;

/** @typedef {HTMLElement & { source: Element, destination: Element, useSourceSize: string | boolean }} FlyToCart */

export class AddToCartComponent extends Component {
  requiredRefs = ['addToCartButton'];
  #resetTimeouts = [];

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('pointerenter', this.#preloadImage);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#resetTimeouts.forEach((timeoutId) => clearTimeout(timeoutId));
    this.removeEventListener('pointerenter', this.#preloadImage);
  }

  disable() {
    this.refs.addToCartButton.disabled = true;
  }

  enable() {
    this.refs.addToCartButton.disabled = false;
  }

  handleClick(event) {
    const form = this.closest('form');
    if (!form?.checkValidity()) return;

    const productForm = this.closest('product-form-component');
    const quantitySelector = productForm?.refs.quantitySelector;
    if (quantitySelector?.canAddToCart) {
      const validation = quantitySelector.canAddToCart();
      if (!validation.canAdd) return;
    }

    if (this.refs.addToCartButton.dataset.puppet !== 'true') {
      const animationEnabled = this.dataset.addToCartAnimation === 'true';
      if (animationEnabled && !event.target.closest('.quick-add-modal')) this.#animateFlyToCart();
      this.animateAddToCart();
    }
  }

  #preloadImage = () => {
    const image = this.dataset.productVariantMedia;
    if (image) preloadImage(image);
  };

  #animateFlyToCart() {
    const { addToCartButton } = this.refs;
    const cartIcon = document.querySelector('.header-actions__cart-icon');
    const image = this.dataset.productVariantMedia;
    if (!cartIcon || !addToCartButton || !image) return;

    const flyToCartElement = document.createElement('fly-to-cart');
    flyToCartElement.classList.add(addToCartButton.classList.contains('quick-add__button') ? 'fly-to-cart--quick' : 'fly-to-cart--main');
    flyToCartElement.style.setProperty('background-image', `url(${image})`);
    flyToCartElement.style.setProperty('--start-opacity', '0');
    flyToCartElement.source = addToCartButton;
    flyToCartElement.destination = cartIcon;
    document.body.appendChild(flyToCartElement);
  }

  animateAddToCart = async function () {
    this.#resetTimeouts.forEach((timeoutId) => clearTimeout(timeoutId));
    this.#resetTimeouts = [];
    this.refs.addToCartButton.dataset.added = 'true';
    await yieldToMainThread();
    await onAnimationEnd(this.refs.addToCartButton);
    const timeoutId = setTimeout(() => {
      this.refs.addToCartButton.removeAttribute('data-added');
      const index = this.#resetTimeouts.indexOf(timeoutId);
      if (index > -1) this.#resetTimeouts.splice(index, 1);
    }, 800);
    this.#resetTimeouts.push(timeoutId);
  };
}

if (!customElements.get('add-to-cart-component')) customElements.define('add-to-cart-component', AddToCartComponent);

class ProductFormComponent extends Component {
  requiredRefs = ['variantId', 'liveRegion'];
  #abortController = new AbortController();
  #timeout;
  #variantChangeInProgress = false;
  #variantChangeGeneration = 0;
  #addToCartQueue = [];

  connectedCallback() {
    super.connectedCallback();
    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section, dialog, product-card');
    target?.addEventListener(StandardEvents.productSelect, this.#onProductSelect, { signal });
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#onCartUpdate, { signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  #getVariantIdInput() {
    return this.querySelector('input[name="id"]')?.value;
  }

  async #refreshCart() {
    const cartItemsComponent = document.querySelector('cart-items-component');
    if (cartItemsComponent) {
      await customElements.whenDefined('cart-items-component');
      return cartItemsComponent.fetchCartData();
    }
    return fetch(`${Theme.routes.cart_url}.json`, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    }).then((response) => {
      if (!response.ok) throw new Error(`Failed to fetch cart: ${response.status}`);
      return response.json();
    });
  }

  #updateCartQuantity(cart) {
    const variantIdInput = this.#getVariantIdInput();
    if (!variantIdInput) return;
    const cartItem = cart.items.find((item) => item.variant_id.toString() === variantIdInput.toString());
    const cartQty = cartItem ? cartItem.quantity : 0;
    const quantitySelector = this.querySelector('quantity-selector-component');
    if (quantitySelector?.setCartQuantity) quantitySelector.setCartQuantity(cartQty);
    this.#updateQuantityLabel(cartQty);
  }

  #onCartUpdate = async (event) => {
    if (!this.#getVariantIdInput()) return;
    event.promise?.then(({ detail }) => {
      if (detail?.sourceId === this.id || detail?.source === 'product-form-component') return;
      if (detail?.items) this.#updateCartQuantity({ items: detail.items });
      else this.#refreshCart().then((cart) => this.#updateCartQuantity(cart));
    }).catch((error) => {
      if (error?.name !== 'AbortError') console.warn('[product-form] Event promise rejected:', error);
    });
  };

  handleSubmit(event) {
    event.preventDefault();
    if (this.#variantChangeInProgress) {
      const intendedVariantId = this.#getIntendedVariantId();
      const quantity = this.#getQuantity();
      if (intendedVariantId) this.#addToCartQueue.push({ variantId: intendedVariantId, quantity });
      this.refs.addToCartButtonContainer?.animateAddToCart?.();
      return;
    }
    this.#processAddToCart(undefined, undefined, event);
  }

  #getIntendedVariantId() {
    return new URL(window.location.href).searchParams.get('variant') || this.refs.variantId?.value || undefined;
  }

  #getQuantity() {
    return Number(this.refs.quantitySelector?.getValue?.()) || Number(this.dataset.quantityDefault) || 1;
  }

  #processAddToCart(overrideVariantId, overrideQuantity, event) {
    const { addToCartTextError } = this.refs;
    if (this.#timeout) clearTimeout(this.#timeout);

    const allAddToCartContainers = this.querySelectorAll('add-to-cart-component');
    if (!overrideVariantId && Array.from(allAddToCartContainers).some((container) => container.refs.addToCartButton?.disabled)) return;

    const form = this.querySelector('form');
    if (!form) throw new Error('Product form element missing');

    if (!overrideVariantId && this.refs.quantitySelector?.canAddToCart) {
      const validation = this.refs.quantitySelector.canAddToCart();
      if (!validation.canAdd) {
        allAddToCartContainers.forEach((container) => container.disable());
        const errorTemplate = this.dataset.quantityErrorMax || '';
        const errorMessage = errorTemplate.replace('{{ maximum }}', validation.maxQuantity?.toString() || '');
        if (addToCartTextError) {
          addToCartTextError.classList.remove('hidden');
          const textNode = addToCartTextError.childNodes[2];
          if (textNode) textNode.textContent = errorMessage;
          else addToCartTextError.appendChild(document.createTextNode(errorMessage));
          this.#setLiveRegionText(errorMessage);
          this.#timeout = setTimeout(() => {
            addToCartTextError.classList.add('hidden');
            this.#clearLiveRegionText();
          }, ERROR_MESSAGE_DISPLAY_DURATION);
        }
        setTimeout(() => allAddToCartContainers.forEach((container) => container.enable()), ERROR_BUTTON_REENABLE_DELAY);
        return;
      }
    }

    const formData = new FormData(form);
    if (overrideVariantId) formData.set('id', overrideVariantId);
    if (overrideQuantity !== undefined) formData.set('quantity', overrideQuantity.toString());

    const cartItemComponentsSectionIds = [];
    document.querySelectorAll('cart-items-component').forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) cartItemComponentsSectionIds.push(item.dataset.sectionId);
    });
    formData.delete('sections');
    if (cartItemComponentsSectionIds.length) formData.set('sections', cartItemComponentsSectionIds.join(','));

    const itemCount = Number(formData.get('quantity')) || Number(this.dataset.quantityDefault) || 1;
    const deferredEventPromise = CartLinesUpdateEvent.createPromise();

    this.dispatchEvent(new CartLinesUpdateEvent({
      action: 'add',
      context: 'product',
      lines: [{ merchandiseId: formData.get('id'), quantity: itemCount }],
      promise: deferredEventPromise.promise,
    }));

    const fetchCfg = fetchConfig('javascript', { body: formData });
    fetch(Theme.routes.cart_add_url, {
      ...fetchCfg,
      headers: {
        ...fetchCfg.headers,
        Accept: 'application/json',
      },
    })
      .then((response) => response.json())
      .then(async (response) => {
        if (response.status) {
          this.dispatchEvent(new CartErrorEvent({
            error: response.message || 'Add to cart failed',
            code: 'INVALID',
            detail: { description: response.description, errors: response.errors },
          }));
          this.#refreshCart()
            .then((ajaxCart) => deferredEventPromise.resolve({
              cart: CartLinesUpdateEvent.createCartFromAjaxResponse(ajaxCart),
              detail: { didError: true, items: ajaxCart.items, source: 'product-form-component', sourceId: this.id.toString(), itemCount, productId: this.dataset.productId },
            }))
            .catch(deferredEventPromise.reject);
          if (!addToCartTextError) return;
          addToCartTextError.classList.remove('hidden');
          const textNode = addToCartTextError.childNodes[2];
          if (textNode) textNode.textContent = response.message;
          else addToCartTextError.appendChild(document.createTextNode(response.message));
          this.#setLiveRegionText(response.message);
          this.#timeout = setTimeout(() => {
            addToCartTextError.classList.add('hidden');
            this.#clearLiveRegionText();
          }, ERROR_MESSAGE_DISPLAY_DURATION);
          return;
        }

        if (addToCartTextError) {
          addToCartTextError.classList.add('hidden');
          addToCartTextError.removeAttribute('aria-live');
        }

        const id = formData.get('id');
        if (!id) throw new Error('Form ID is required');

        const anyAddToCartButton = allAddToCartContainers[0]?.refs.addToCartButton;
        if (anyAddToCartButton) {
          const addedTextElement = anyAddToCartButton.querySelector('.add-to-cart-text--added');
          const addedText = addedTextElement?.textContent?.trim() || Theme.translations.added;
          this.#setLiveRegionText(addedText);
          setTimeout(() => this.#clearLiveRegionText(), SUCCESS_MESSAGE_DISPLAY_DURATION);
        }

        await this.#refreshCart().then((ajaxCart) => {
          deferredEventPromise.resolve({
            cart: CartLinesUpdateEvent.createCartFromAjaxResponse(ajaxCart),
            detail: { items: ajaxCart.items, source: 'product-form-component', sourceId: this.id.toString(), itemCount, productId: this.dataset.productId, sections: response.sections, didError: false },
          });
          this.#updateCartQuantity(ajaxCart);
        });
      })
      .catch((error) => {
        console.error(error);
        deferredEventPromise.reject(error);
        this.dispatchEvent(new CartErrorEvent({ error: error?.message || 'Network error during add to cart', code: 'SERVICE_UNAVAILABLE' }));
      })
      .finally(() => {
        if (event) cartPerformance.measureFromEvent('add:user-action', event);
      });
  }

  #processBatchAddToCart(items) {
    if (items.length === 0) return;
    const { addToCartTextError } = this.refs;
    if (this.#timeout) clearTimeout(this.#timeout);

    const cartItemComponentsSectionIds = [];
    document.querySelectorAll('cart-items-component').forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) cartItemComponentsSectionIds.push(item.dataset.sectionId);
    });

    const totalQuantity = items.reduce((sum, item) => sum + item.quantity, 0);
    const deferredEventPromise = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(new CartLinesUpdateEvent({
      action: 'add',
      context: 'product',
      lines: items.map((item) => ({ merchandiseId: item.variantId, quantity: item.quantity })),
      promise: deferredEventPromise.promise,
    }));

    const payload = {
      items: items.map((item) => ({ id: Number(item.variantId), quantity: item.quantity })),
      sections: cartItemComponentsSectionIds.join(','),
    };

    fetch(Theme.routes.cart_add_url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(payload),
    })
      .then((response) => response.json())
      .then(async (response) => {
        if (response.status) {
          this.dispatchEvent(new CartErrorEvent({ error: response.message || 'Add to cart failed', code: 'INVALID', detail: { description: response.description, errors: response.errors } }));
          this.#refreshCart().then((ajaxCart) => deferredEventPromise.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(ajaxCart), detail: { didError: true, items: ajaxCart.items, source: 'product-form-component', sourceId: this.id.toString(), itemCount: totalQuantity, productId: this.dataset.productId } })).catch(deferredEventPromise.reject);
          if (addToCartTextError) {
            addToCartTextError.classList.remove('hidden');
            const textNode = addToCartTextError.childNodes[2];
            if (textNode) textNode.textContent = response.message;
            else addToCartTextError.appendChild(document.createTextNode(response.message));
            this.#setLiveRegionText(response.message);
            this.#timeout = setTimeout(() => { addToCartTextError.classList.add('hidden'); this.#clearLiveRegionText(); }, ERROR_MESSAGE_DISPLAY_DURATION);
          }
          return;
        }

        if (addToCartTextError) {
          addToCartTextError.classList.add('hidden');
          addToCartTextError.removeAttribute('aria-live');
        }

        const allAddToCartContainers = this.querySelectorAll('add-to-cart-component');
        const anyAddToCartButton = allAddToCartContainers[0]?.refs.addToCartButton;
        if (anyAddToCartButton) {
          const addedTextElement = anyAddToCartButton.querySelector('.add-to-cart-text--added');
          const addedText = addedTextElement?.textContent?.trim() || Theme.translations.added;
          this.#setLiveRegionText(addedText);
          setTimeout(() => this.#clearLiveRegionText(), SUCCESS_MESSAGE_DISPLAY_DURATION);
        }

        const cart = await this.#refreshCart();
        deferredEventPromise.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
          detail: { items: cart.items, source: 'product-form-component', sourceId: this.id.toString(), itemCount: totalQuantity, productId: this.dataset.productId, sections: response.sections, didError: false },
        });
        this.#updateCartQuantity(cart);
      })
      .catch((error) => {
        console.error(error);
        deferredEventPromise.reject(error);
        this.dispatchEvent(new CartErrorEvent({ error: error?.message || 'Network error during add to cart', code: 'SERVICE_UNAVAILABLE' }));
      });
  }

  #updateQuantityLabel(cartQty) {
    const quantityLabel = this.refs.quantityLabelCartCount;
    if (quantityLabel) {
      const inCartText = quantityLabel.textContent?.match(/\((\d+)\s+(.+)\)/);
      if (inCartText && inCartText[2]) quantityLabel.textContent = `(${cartQty} ${inCartText[2]})`;
      quantityLabel.classList.toggle('hidden', cartQty === 0);
    }
  }

  #setLiveRegionText(text) {
    this.refs.liveRegion.textContent = text;
  }

  #clearLiveRegionText() {
    this.refs.liveRegion.textContent = '';
  }

  #morphOrUpdateElement(currentElement, newElement, insertReferenceElement = null) {
    if (currentElement && newElement) morph(currentElement, newElement);
    else if (currentElement && !newElement) currentElement.remove();
    else if (!currentElement && newElement && insertReferenceElement) insertReferenceElement.insertAdjacentElement('beforebegin', newElement.cloneNode(true));
  }

  #onProductSelect = async (event) => {
    const sourceCard = event.target?.closest('product-card');
    if (sourceCard && !sourceCard.contains(this)) return;
    const generation = ++this.#variantChangeGeneration;
    this.#variantChangeInProgress = true;
    try {
      const { detail } = await event.promise;
      if (!detail?.html) return;
      const { html, newProduct, productId, resource } = detail;
      if (newProduct) this.dataset.productId = newProduct.id;
      else if (productId && productId !== this.dataset.productId) return;

      const { variantId } = this.refs;
      variantId.value = resource?.id ?? '';
      const { addToCartButtonContainer: currentAddToCartButtonContainer, acceleratedCheckoutButtonContainer } = this.refs;
      const currentAddToCartButton = currentAddToCartButtonContainer?.refs.addToCartButton;
      if (!currentAddToCartButtonContainer || (!currentAddToCartButton && !acceleratedCheckoutButtonContainer)) return;

      if (resource == null || resource.available == false) currentAddToCartButtonContainer.disable();
      else currentAddToCartButtonContainer.enable();

      const newAddToCartButton = html.querySelector('product-form-component [ref="addToCartButton"]');
      if (newAddToCartButton && currentAddToCartButton) morph(currentAddToCartButton, newAddToCartButton);

      if (acceleratedCheckoutButtonContainer) {
        if (resource == null || resource.available == false) acceleratedCheckoutButtonContainer.setAttribute('hidden', 'true');
        else acceleratedCheckoutButtonContainer.removeAttribute('hidden');
      }

      if (resource) {
        const productVariantMedia = resource.featured_media?.preview_image?.src;
        if (productVariantMedia) currentAddToCartButtonContainer.setAttribute('data-product-variant-media', productVariantMedia + '&width=100');
      }

      const { quantityRules, pricePerItem, quantitySelector, productFormButtons, quantityLabel, quantitySelectorWrapper } = this.refs;
      const newQuantityInput = html.querySelector('quantity-selector-component input[ref="quantityInput"]');
      if (quantitySelector?.updateConstraints && newQuantityInput) {
        quantitySelector.updateConstraints(newQuantityInput.min, newQuantityInput.max || null, newQuantityInput.step);
        this.dataset.quantityDefault = newQuantityInput.min || '1';
      }

      const newQuantityRules = html.querySelector('.quantity-rules');
      const isQuantityRulesChanging = !!quantityRules !== !!newQuantityRules;
      const newPricePerItem = html.querySelector('price-per-item');
      const isPricePerItemChanging = !!pricePerItem !== !!newPricePerItem;

      if ((isQuantityRulesChanging || isPricePerItemChanging) && quantitySelector) {
        const currentQuantityValue = quantitySelector.getValue?.();
        const newProductFormButtons = html.querySelector('.product-form-buttons');
        if (productFormButtons && newProductFormButtons) {
          morph(productFormButtons, newProductFormButtons);
          const newQuantityInputElement = html.querySelector('quantity-selector-component input[ref="quantityInput"]');
          if (this.refs.quantitySelector?.updateConstraints && newQuantityInputElement && currentQuantityValue) {
            this.refs.quantitySelector.setValue(currentQuantityValue);
            this.refs.quantitySelector.updateConstraints(newQuantityInputElement.min, newQuantityInputElement.max || null, newQuantityInputElement.step);
            this.dataset.quantityDefault = newQuantityInputElement.min || '1';
          }
        }
      } else {
        const morphTargets = [
          ['.quantity-label', quantityLabel, quantitySelector],
          ['.quantity-rules', quantityRules, this.refs.productFormButtons],
          ['price-per-item', pricePerItem, quantitySelectorWrapper],
        ];
        for (const [selector, currentElement, fallback] of morphTargets) this.#morphOrUpdateElement(currentElement, html.querySelector(selector), fallback);
      }

      const currentVolumePricing = this.refs.volumePricing;
      const newVolumePricing = html.querySelector('volume-pricing');
      this.#morphOrUpdateElement(currentVolumePricing, newVolumePricing, this.refs.productFormButtons);

      const hasB2BFeatures = quantityRules || newQuantityRules || pricePerItem || newPricePerItem || currentVolumePricing || newVolumePricing;
      if (!hasB2BFeatures) return;
      this.#refreshCart().then((cart) => this.#updateCartQuantity(cart));
    } finally {
      if (generation === this.#variantChangeGeneration) {
        this.#variantChangeInProgress = false;
        if (this.#addToCartQueue.length > 0) {
          const queuedItems = [...this.#addToCartQueue];
          this.#addToCartQueue = [];
          this.#processBatchAddToCart(queuedItems);
        }
      }
    }
  };
}

if (!customElements.get('product-form-component')) customElements.define('product-form-component', ProductFormComponent);
